import { useSignal } from '@preact/signals';

import { t } from '@amumin/i18n';
import { Button, Notice, TextField } from '@amumin/ui';

import { api, errorMessage } from '../api.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { normalizeEmail, validateRegister } from '../lib/validation.js';
import { browserLocale, messages } from '../state/session.js';

/** Registratie: e-mail + wachtwoord >= 12 tekens; breach-check en verificatiemail server-side. */
export function RegisterPage() {
  const m = messages.value;
  const email = useSignal('');
  const password = useSignal('');
  const repeat = useSignal('');
  const errors = useSignal<{
    email?: string | undefined;
    password?: string | undefined;
    repeat?: string | undefined;
  }>({});
  const error = useSignal<string | undefined>(undefined);
  const busy = useSignal(false);
  const done = useSignal<string | null>(null);

  const submit = async () => {
    const v = validateRegister({
      email: email.value,
      password: password.value,
      repeat: repeat.value,
    });
    errors.value = {
      email: v.email ? m.web[v.email] : undefined,
      password: v.password ? m.web[v.password] : undefined,
      repeat: v.repeat ? m.web[v.repeat] : undefined,
    };
    if (v.email || v.password || v.repeat) return;
    busy.value = true;
    error.value = undefined;
    try {
      const normalized = normalizeEmail(email.value);
      // Golf 3: de taal van dit scherm wordt de taal van het nieuwe gezin (later te wijzigen).
      await api.app.auth.register({
        email: normalized,
        password: password.value,
        locale: browserLocale.value,
      });
      done.value = normalized;
    } catch (e) {
      error.value = errorMessage(e, m);
    } finally {
      busy.value = false;
    }
  };

  if (done.value) {
    return (
      <AuthLayout title={m.web.registerDone}>
        <Notice kind="success" name="register-done">
          {t(m.web.registerDoneText, { email: done.value })}
        </Notice>
        <p class="auth-links">
          <a href="/login">{m.web.toLogin}</a>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={m.web.registerTitle} illustration="welcome">
      {error.value ? <Notice kind="error">{error.value}</Notice> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <TextField
          id="email"
          label={m.web.email}
          type="email"
          value={email.value}
          onInput={(v) => (email.value = v)}
          autoComplete="username"
          inputMode="email"
          error={errors.value.email}
          hint={m.web.privateEmailHint}
          required
        />
        <TextField
          id="password"
          label={m.web.password}
          type="password"
          value={password.value}
          onInput={(v) => (password.value = v)}
          autoComplete="new-password"
          error={errors.value.password}
          hint={m.web.passwordHint}
          required
        />
        <TextField
          id="password-repeat"
          label={m.web.passwordRepeat}
          type="password"
          value={repeat.value}
          onInput={(v) => (repeat.value = v)}
          autoComplete="new-password"
          error={errors.value.repeat}
          required
        />
        <Button name="register" type="submit" variant="primary" block busy={busy.value}>
          {m.web.register}
        </Button>
        <p class="auth-links">
          {m.web.alreadyAccount} <a href="/login">{m.web.login}</a>
        </p>
      </form>
    </AuthLayout>
  );
}
